// @flow

import React, { useContext, useState } from 'react';
import {
  FLINK_POOL_RESOURCES_GRID_FIELDNAMES,
  FLINK_POOL_RESOURCES_GRID_HEADERS,
} from '@streaming-projects/orgs/enums';
import { StyledContainer } from '@src/common-utils/styledComponents';
import { ConfirmModal } from '@presentational/modals/ConfirmModal';
import { DataGrid } from '@components/views/grid-utils/DataGrid';
import {
  useAddFlinkWorkloadMutation,
  useGetAvailableFlinkPoolResourcesQuery,
} from '@streaming-projects/service-definitions/streamingProjectsApi';
import { handleGridRowSelectionChanged } from '@src/common-utils/utils';
import { useHistory, useParams } from 'react-router-dom';
import { SPStreamingProjectContext } from '@streaming-projects/sp-page/contexts/SPStreamingProjectContext';
import { toastError } from '@presentational/notifications/utils';
import { getLinkForStreamingProjectsFlinkWorkloadsPage } from '@streaming-projects/links';
import {
  getMappedDataValueGetter,
  getRegionName,
} from '@streaming-projects/resource-definitions/auxilary-components/MappedDataRenderer';
import { SPMetaDataContext } from '@streaming-projects/orgs/contexts/SPMetaDataContext';
import { ADD_NEW_WORKLOAD_MESSAGE, NO_FREE_RESOURCE_MESSAGE } from '@streaming-projects/constants';
import { ResourceSelectionRadioButtonRenderer } from '@components/views/grid-utils/renderers';
import { QueryError } from '@presentational/QueryError';

export const SPFlinkWorkloadAddModal = ({ isOpen, setOpen }) => {
  const { orgId, spId } = useParams();
  const { providerRegionsMap, providerNameLabelMap } = useContext(SPMetaDataContext).metaData;
  const [addFlinkWorkload] = useAddFlinkWorkloadMutation();
  const { push } = useHistory();

  const spStreamingProjectData = useContext(SPStreamingProjectContext);

  const [selectedRow, setSelectedRow] = useState(null);

  const columnDefs = [
    {
      field: 'Selection',
      sortable: false,
      filter: false,
      cellRenderer: (props) =>
        ResourceSelectionRadioButtonRenderer(
          props,
          selectedRow,
          FLINK_POOL_RESOURCES_GRID_HEADERS.ID
        ),
    },
    {
      field: FLINK_POOL_RESOURCES_GRID_HEADERS.ID,
      backEndFieldName: FLINK_POOL_RESOURCES_GRID_FIELDNAMES.ID,
      hide: true,
    },
    {
      field: FLINK_POOL_RESOURCES_GRID_HEADERS.NAME,
      backEndFieldName: FLINK_POOL_RESOURCES_GRID_FIELDNAMES.NAME,
    },
    {
      field: FLINK_POOL_RESOURCES_GRID_HEADERS.PROVIDER,
      backEndFieldName: FLINK_POOL_RESOURCES_GRID_FIELDNAMES.PROVIDER,
      valueGetter: getMappedDataValueGetter(providerNameLabelMap),
    },
    {
      field: FLINK_POOL_RESOURCES_GRID_HEADERS.REGION,
      backEndFieldName: FLINK_POOL_RESOURCES_GRID_FIELDNAMES.REGION,
      tooltipValueGetter: (params) => getRegionName(params, providerRegionsMap),
      valueGetter: (params) => getRegionName(params, providerRegionsMap),
    },
  ];

  const onSelectionChanged = handleGridRowSelectionChanged(
    (rowData) => setSelectedRow(rowData),
    () => setSelectedRow(null)
  );

  // Note: We cannot use DataFetcherComponent here as we need more control
  const { currentData, error, isLoading, isFetching } = useGetAvailableFlinkPoolResourcesQuery(
    { orgId, spId },
    {
      refetchOnMountOrArgChange: true,
      skip: !orgId || !isOpen,
    }
  );

  if (isLoading || isFetching || !isOpen) {
    return null;
  }

  if (error) {
    return <QueryError error={error} />;
  }

  const resourcesToUse = currentData?.resources?.flink_resources ?? [];

  return (
    <StyledContainer>
      <ConfirmModal
        body={
          <StyledContainer>
            <DataGrid
              columnDefs={columnDefs}
              inputDataSource={resourcesToUse}
              noDataMessage={NO_FREE_RESOURCE_MESSAGE}
              onSelectionChanged={onSelectionChanged}
              rowSelection="single"
              sizeColumnsToFitInitially={true}
              tooltipShowDelay={0}
            />
          </StyledContainer>
        }
        cancelButtonNegative={true}
        disabled={!selectedRow}
        fullScreen={true}
        header={ADD_NEW_WORKLOAD_MESSAGE}
        isOpen={isOpen}
        okButtonNegative={false}
        okButtonText="Continue"
        onClickHandlerForCancel={() => {
          setSelectedRow(null);
          setOpen(false);
        }}
        onClickHandlerForOK={async () => {
          const { error, data } = await addFlinkWorkload({
            orgId,
            spId,
            payload: {
              name: 'Default',
              flink_pool_config_id: selectedRow['Flink Pool ID'],
              sp_version: spStreamingProjectData?.sp?.version,
            },
          });
          if (error) {
            toastError(error);
          } else {
            push(getLinkForStreamingProjectsFlinkWorkloadsPage(orgId, spId, data.flink_pool_id));
          }
          setOpen(false);
        }}
      />
    </StyledContainer>
  );
};
